import { Badge, Box } from "@chakra-ui/layout";
import { useColorModeValue } from "@chakra-ui/react";
import styled from "@emotion/styled";
import React from "react";
import useQuote from "../../hooks/useQuote";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { YesNo } from "../../lib/models/enums";
import { RentalScenarioCalculation } from "../../lib/models/rental/rental-calculation.model";
import { Scenario } from "../../lib/models/rental/rental.model";
import { formatMoney, toFixed } from "../../lib/number-transforms";
import { QuoteUtils } from "../../lib/quote-utils";
import FormSectionHeader from "../common-quote/FormSectionHeader";
import RadioField from "../form-fields/RadioField";
import SwitchField from "../form-fields/SwitchField";
import RentalScenarioTableInput from "./RentalScenarioTableInput";
import RentalScenarioTableNumber from "./RentalScenarioTableNumber";

function RentalScenarios({
  scenarios,
  setPreferredLoanAmt,
}: {
  actionHandler?: () => void;
  scenarios: RentalScenarioCalculation[];
  setPreferredLoanAmt: any;
}) {
  const quoteContext = useQuote();
  const { state, dispatch } = quoteContext;
  const { metadata } = state;
  const { isNewQuote } = state;
  const [selectedLtvValue, setSelectedLtvValue] = React.useState<number>();
  const preferredScenario = state.rentalQuoteScenarios.find(
    (x) => x.preferred === YesNo.Yes
  );
  const headerBg = useColorModeValue(lightMode[100], darkMode[200]);
  const headerTextColor = useColorModeValue("#777676", "white");
  const primaryScenarioColor = useColorModeValue("#E2E8F0", "#353e4f");

  React.useEffect(() => {
    if (isNewQuote) {
      dispatch({
        type: "SCENARIO_LOADED",
        payload: QuoteUtils.GenerateRentalQuoteScenarios(),
      });
    }
  }, [isNewQuote, dispatch]);

  const updateScenario = React.useCallback(
    (id: number, update: Partial<Scenario>): Scenario | null => {
      const scenario = state.rentalQuoteScenarios.find(
        (x) => x.scenarioId === id
      );

      if (!scenario) return null;

      const updateFields = {
        ...scenario,
        ...update,
      };

      dispatch({
        type: "SCENARIO_UPDATE",
        payload: updateFields,
      });

      return updateFields;
    },
    [dispatch, state.rentalQuoteScenarios]
  );

  const onScenarioLTVUpdate = (scenarioId: number, updatedValue: number) => {
    updateScenario(scenarioId, { ltvPercentage: updatedValue });
  };

  const onScenarioQuoteToggle = (
    scenarioId: number,
    includedInQuote: YesNo
  ) => {
    updateScenario(scenarioId, { included: includedInQuote });
  };

  const onPreferredScenarioToggle = React.useCallback(
    (scenarioId: number) => {
      updateScenario(scenarioId, { preferred: YesNo.Yes });

      setSelectedLtvValue(scenarioId);
    },
    [updateScenario]
  );

  React.useEffect(() => {
    if (!scenarios) return;
    const scenario = state.rentalQuoteScenarios.find(
      (x) => x.preferred === YesNo.Yes
    );

    if (!scenario) {
      onPreferredScenarioToggle(1);
    }

    setSelectedLtvValue(scenario?.scenarioId ?? 1);

    if (preferredScenario) {
      const preferredScenarioLoanAmt =
        scenarios[(preferredScenario.scenarioId ?? 1) - 1].loanAmt;
      setPreferredLoanAmt(preferredScenarioLoanAmt);
    }
  }, [
    scenarios,
    state.rentalQuoteScenarios,
    onPreferredScenarioToggle,
    setPreferredLoanAmt,
    preferredScenario,
  ]);
  return (
    <section>
      <FormSectionHeader id="scenarios" label="Scenarios" />
      <Box overflow="auto">
        <ScenarioTable
          headerBg={headerBg}
          headerTextColor={headerTextColor}
          primaryScenarioColor={primaryScenarioColor}
        >
          {metadata?.underwriterStatus !== "Not Submitted" && (
            <>
              <thead>
                <tr>
                  <td></td>
                  <td className="underwriting-scenario-text">
                    Underwriting Scenario
                  </td>
                </tr>
              </thead>
              <colgroup>
                <col />
                <col className="dashed-box" />
              </colgroup>
            </>
          )}
          <tbody>
            <tr>
              <th>Loan to Value</th>
              {state.rentalQuoteScenarios.map((scenario) => (
                <td key={scenario.scenarioId} className="center-text">
                  <RentalScenarioTableInput
                    changeHandler={(value) =>
                      onScenarioLTVUpdate(scenario.scenarioId, value)
                    }
                    inputValue={scenario.ltvPercentage.toString()}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <th>Preferred Scenario</th>
              {state.rentalQuoteScenarios.map((scenario) => (
                <td key={scenario.scenarioId} className="center-text">
                  <RadioField
                    isChecked={selectedLtvValue === scenario.scenarioId}
                    value={scenario.scenarioId}
                    onChange={(e: any) =>
                      onPreferredScenarioToggle(+e.target.value)
                    }
                    name={`preferredScenario${scenario.scenarioId}`}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <th>Include in Quote</th>
              {state.rentalQuoteScenarios.map((scenario) => (
                <td key={scenario.scenarioId} className="center-text">
                  <SwitchField
                    changeHandler={(includedInQuote) =>
                      onScenarioQuoteToggle(
                        scenario.scenarioId,
                        includedInQuote
                      )
                    }
                    name={`scenario${scenario.scenarioId}`}
                    inputValue={scenario.included}
                    size="md"
                    switchProps={{ justifyContent: "center" }}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <th>Eligibility</th>
              {scenarios.map((scenario, index) => (
                <td key={index} className="center-text">
                  <Badge
                    size="md"
                    borderRadius="md"
                    paddingX={3}
                    paddingY={1}
                    colorScheme={
                      scenario.ltvEligibility.toLowerCase() === "eligible"
                        ? "green"
                        : "red"
                    }
                  >
                    {scenario.ltvEligibility}
                  </Badge>
                </td>
              ))}
            </tr>
            <tr>
              <th>Loan Amount</th>
              {scenarios.map((scenario, index) => {
                return (
                  <td key={index}>
                    <RentalScenarioTableNumber
                      key={scenario.loanAmt}
                      label={formatMoney(scenario.loanAmt)}
                      symbol="$"
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <th>Cash To</th>
              {scenarios.map((scenario, index) => {
                return (
                  <td key={index}>
                    <RentalScenarioTableNumber
                      label={formatMoney(scenario.closingCashToBorrower)}
                      symbol="$"
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <th>Interest Rate</th>
              {scenarios.map((scenario, index) => {
                return (
                  <td key={index}>
                    <RentalScenarioTableNumber
                      label={toFixed(
                        state.quoteType === "Rental"
                          ? scenario.initFixedInterestRate
                          : scenario.premier?.interestRateValue ?? 0,
                        3
                      )}
                      symbol="%"
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <th>Origination Fee</th>
              {scenarios.map((scenario, index) => {
                return (
                  <td key={index}>
                    <RentalScenarioTableNumber
                      label={formatMoney(scenario.originationFee)}
                      symbol="$"
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <th>Buy Up / Buy Down Fee</th>
              {scenarios.map((scenario, index) => {
                return (
                  <td key={index}>
                    <RentalScenarioTableNumber
                      label={formatMoney(scenario.buyUpDownFee, 2)}
                      symbol="$"
                    />
                  </td>
                );
              })}
            </tr>
            <tr>
              <th>RDSR</th>
              {scenarios.map((scenario, index) => {
                return (
                  <td key={index}>
                    <RentalScenarioTableNumber
                      label={toFixed(scenario.rentDebtRatio, 3)}
                      symbol=""
                    />
                  </td>
                );
              })}
            </tr>
          </tbody>
        </ScenarioTable>
      </Box>
    </section>
  );
}

const ScenarioTable = styled.table<{
  headerBg: string;
  headerTextColor: string;
  primaryScenarioColor: string;
}>`
  width: 100%;
  margin-top: var(--chakra-space-4);

  tr:not(:last-of-type) {
    border-bottom: 1px solid var(--chakra-colors-gray-200);
  }

  th,
  td {
    padding: var(--chakra-space-4);
  }

  th {
    color: #777676;
    color: ${(props) => props.headerTextColor};
    font-weight: normal;
    text-align: left;
    left: 0;
    top: 0;
    background: ${(props) => props.headerBg};
    opacity: 1;
    position: sticky;
  }

  td {
    font-weight: 500;
    min-width: 200px;
  }

  .center-text {
    text-align: center;
  }

  .right-text {
    text-align: right;
  }

  .dashed-box {
    border-width: 5px;
    border-style: dashed;
    border-color: ${(props) => props.primaryScenarioColor};
  }

  .underwriting-scenario-text {
    font-weight: normal;
    opacity: 1;
    text-align: center;
    padding-bottom: 0;
    border-bottom: 2px solid ${(props) => props.primaryScenarioColor};
  }
`;

export default RentalScenarios;
